/**
 * Comprehensive error handling utilities for expense management
 */

export enum ExpenseErrorType {
  // Validation Errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  REQUIRED_FIELD_MISSING = 'REQUIRED_FIELD_MISSING',
  INVALID_FORMAT = 'INVALID_FORMAT',
  VALUE_OUT_OF_RANGE = 'VALUE_OUT_OF_RANGE',
  INVALID_DATE_RANGE = 'INVALID_DATE_RANGE',
  
  // Business Logic Errors
  CATEGORY_NOT_FOUND = 'CATEGORY_NOT_FOUND',
  CATEGORY_ACCESS_DENIED = 'CATEGORY_ACCESS_DENIED',
  DUPLICATE_CATEGORY = 'DUPLICATE_CATEGORY',
  EXPENSE_NOT_FOUND = 'EXPENSE_NOT_FOUND',
  EXPENSE_ACCESS_DENIED = 'EXPENSE_ACCESS_DENIED',
  RECURRING_RULE_NOT_FOUND = 'RECURRING_RULE_NOT_FOUND',
  RECURRING_RULE_ACCESS_DENIED = 'RECURRING_RULE_ACCESS_DENIED',
  
  // System Errors
  DATABASE_ERROR = 'DATABASE_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  STORAGE_ERROR = 'STORAGE_ERROR',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  
  // Authentication/Authorization Errors
  AUTHENTICATION_REQUIRED = 'AUTHENTICATION_REQUIRED',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  
  // General Errors
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export interface ExpenseError {
  type: ExpenseErrorType;
  field?: string;
  message: string;
  code?: string;
  statusCode?: number;
  retryable?: boolean;
  retryAfter?: number;
  context?: Record<string, any>;
  suggestedAction?: string;
  helpUrl?: string;
}

export interface ExpenseErrorDisplayInfo {
  title: string;
  message: string;
  details?: string;
  actionText?: string;
  actionUrl?: string;
  retryable: boolean;
  severity: 'error' | 'warning' | 'info';
  icon?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ExpenseError[];
  warnings?: ExpenseError[];
}

/**
 * Comprehensive error handler for expense operations
 */
export class ExpenseErrorHandler {
  
  /**
   * Parses API errors into standardized format
   */
  static parseApiError(error: any): ExpenseError {
    // Handle axios errors
    if (error.response) {
      const { status, data } = error.response;
      
      // Check if backend sent structured error
      if (data && data.type) {
        return {
          type: data.type as ExpenseErrorType,
          field: data.field,
          message: data.message,
          code: data.code,
          statusCode: status,
          retryable: data.retryable || false,
          retryAfter: data.retryAfter,
          context: data.context,
          suggestedAction: data.suggestedAction,
          helpUrl: data.helpUrl
        };
      }
      
      // Handle standard HTTP errors
      return this.createErrorFromStatus(status, data?.message || error.message);
    }
    
    // Handle network errors
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        type: ExpenseErrorType.NETWORK_ERROR,
        message: 'Request timed out',
        retryable: true,
        retryAfter: 30,
        suggestedAction: 'Please check your internet connection and try again'
      };
    }
    
    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      return {
        type: ExpenseErrorType.NETWORK_ERROR,
        message: 'Network connection failed',
        retryable: true,
        retryAfter: 30,
        suggestedAction: 'Please check your internet connection and try again'
      };
    }
    
    // Default unknown error
    return {
      type: ExpenseErrorType.UNKNOWN_ERROR,
      message: error.message || 'An unexpected error occurred',
      retryable: true,
      retryAfter: 60,
      suggestedAction: 'Please try again later'
    };
  }
  
  /**
   * Creates error from HTTP status code
   */
  private static createErrorFromStatus(status: number, message?: string): ExpenseError {
    switch (status) {
      case 400:
        return {
          type: ExpenseErrorType.VALIDATION_ERROR,
          message: message || 'Invalid request',
          statusCode: status,
          retryable: false,
          suggestedAction: 'Please check your input and try again'
        };
      
      case 401:
        return {
          type: ExpenseErrorType.AUTHENTICATION_REQUIRED,
          message: 'Authentication required',
          statusCode: status,
          retryable: false,
          suggestedAction: 'Please log in and try again'
        };
      
      case 403:
        return {
          type: ExpenseErrorType.INSUFFICIENT_PERMISSIONS,
          message: 'Access denied',
          statusCode: status,
          retryable: false,
          suggestedAction: 'You do not have permission to perform this action'
        };
      
      case 404:
        return {
          type: ExpenseErrorType.EXPENSE_NOT_FOUND,
          message: 'Resource not found',
          statusCode: status,
          retryable: false,
          suggestedAction: 'Please check that the resource exists'
        };
      
      case 408:
        return {
          type: ExpenseErrorType.NETWORK_ERROR,
          message: 'Request timeout',
          statusCode: status,
          retryable: true,
          retryAfter: 30,
          suggestedAction: 'Please try again'
        };
      
      case 409:
        return {
          type: ExpenseErrorType.DUPLICATE_CATEGORY,
          message: 'Resource conflict',
          statusCode: status,
          retryable: false,
          suggestedAction: 'Please check for duplicate entries'
        };
      
      case 429:
        return {
          type: ExpenseErrorType.RATE_LIMIT_EXCEEDED,
          message: 'Too many requests',
          statusCode: status,
          retryable: true,
          retryAfter: 300,
          suggestedAction: 'Please wait a few minutes before trying again'
        };
      
      case 500:
        return {
          type: ExpenseErrorType.INTERNAL_ERROR,
          message: 'Server error',
          statusCode: status,
          retryable: true,
          retryAfter: 60,
          suggestedAction: 'Please try again later'
        };
      
      case 503:
        return {
          type: ExpenseErrorType.NETWORK_ERROR,
          message: 'Service temporarily unavailable',
          statusCode: status,
          retryable: true,
          retryAfter: 300,
          suggestedAction: 'Please try again in a few minutes'
        };
      
      default:
        return {
          type: ExpenseErrorType.UNKNOWN_ERROR,
          message: message || `HTTP ${status} error`,
          statusCode: status,
          retryable: status >= 500,
          retryAfter: 60,
          suggestedAction: 'Please try again later'
        };
    }
  }
  
  /**
   * Formats error for user display
   */
  static formatForDisplay(error: ExpenseError): ExpenseErrorDisplayInfo {
    const errorMap: Partial<Record<ExpenseErrorType, Partial<ExpenseErrorDisplayInfo>>> = {
      [ExpenseErrorType.VALIDATION_ERROR]: {
        title: 'Validation Error',
        severity: 'error',
        icon: '⚠️'
      },
      [ExpenseErrorType.REQUIRED_FIELD_MISSING]: {
        title: 'Required Field Missing',
        severity: 'error',
        icon: '📝'
      },
      [ExpenseErrorType.INVALID_FORMAT]: {
        title: 'Invalid Format',
        severity: 'error',
        icon: '❌'
      },
      [ExpenseErrorType.VALUE_OUT_OF_RANGE]: {
        title: 'Invalid Value',
        severity: 'error',
        icon: '📊'
      },
      [ExpenseErrorType.CATEGORY_NOT_FOUND]: {
        title: 'Category Not Found',
        severity: 'error',
        icon: '🏷️'
      },
      [ExpenseErrorType.DUPLICATE_CATEGORY]: {
        title: 'Duplicate Category',
        severity: 'warning',
        icon: '🔄'
      },
      [ExpenseErrorType.EXPENSE_NOT_FOUND]: {
        title: 'Expense Not Found',
        severity: 'error',
        icon: '💰'
      },
      [ExpenseErrorType.RATE_LIMIT_EXCEEDED]: {
        title: 'Rate Limit Exceeded',
        severity: 'warning',
        icon: '⏱️'
      },
      [ExpenseErrorType.NETWORK_ERROR]: {
        title: 'Connection Error',
        severity: 'warning',
        icon: '🌐'
      },
      [ExpenseErrorType.AUTHENTICATION_REQUIRED]: {
        title: 'Authentication Required',
        severity: 'error',
        icon: '🔐'
      },
      [ExpenseErrorType.INSUFFICIENT_PERMISSIONS]: {
        title: 'Access Denied',
        severity: 'error',
        icon: '🚫'
      },
      [ExpenseErrorType.INTERNAL_ERROR]: {
        title: 'Server Error',
        severity: 'error',
        icon: '⚙️'
      },
      [ExpenseErrorType.UNKNOWN_ERROR]: {
        title: 'Unexpected Error',
        severity: 'error',
        icon: '❓'
      }
    };
    
    const errorInfo = errorMap[error.type] || {};
    
    let message = error.message;
    if (error.suggestedAction) {
      message += `\n\n${error.suggestedAction}`;
    }
    
    let actionText = 'Try Again';
    let actionUrl = error.helpUrl;
    
    if (error.retryAfter) {
      const minutes = Math.ceil(error.retryAfter / 60);
      message += `\n\nYou can try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`;
      actionText = `Retry in ${minutes}m`;
    }
    
    if (error.helpUrl) {
      actionText = 'Get Help';
    }
    
    return {
      title: errorInfo.title || 'Error',
      message,
      details: error.context ? JSON.stringify(error.context, null, 2) : undefined,
      actionText: error.retryable ? actionText : undefined,
      actionUrl,
      retryable: error.retryable || false,
      severity: errorInfo.severity || 'error',
      icon: errorInfo.icon
    };
  }
  
  /**
   * Determines if error should trigger automatic retry
   */
  static shouldAutoRetry(error: ExpenseError, attemptCount: number = 0): boolean {
    if (!error.retryable || attemptCount >= 3) {
      return false;
    }
    
    // Don't auto-retry user errors
    const userErrorTypes = [
      ExpenseErrorType.VALIDATION_ERROR,
      ExpenseErrorType.REQUIRED_FIELD_MISSING,
      ExpenseErrorType.INVALID_FORMAT,
      ExpenseErrorType.VALUE_OUT_OF_RANGE,
      ExpenseErrorType.CATEGORY_NOT_FOUND,
      ExpenseErrorType.EXPENSE_NOT_FOUND,
      ExpenseErrorType.DUPLICATE_CATEGORY,
      ExpenseErrorType.AUTHENTICATION_REQUIRED,
      ExpenseErrorType.INSUFFICIENT_PERMISSIONS
    ];
    
    if (userErrorTypes.includes(error.type)) {
      return false;
    }
    
    // Don't auto-retry rate limits
    if (error.type === ExpenseErrorType.RATE_LIMIT_EXCEEDED) {
      return false;
    }
    
    return true;
  }
  
  /**
   * Calculates retry delay with exponential backoff
   */
  static getRetryDelay(error: ExpenseError, attemptCount: number = 0): number {
    if (error.retryAfter) {
      return error.retryAfter * 1000; // Convert to milliseconds
    }
    
    // Exponential backoff: 2s, 4s, 8s, 16s, etc.
    const baseDelay = 2000;
    return Math.min(baseDelay * Math.pow(2, attemptCount), 30000); // Max 30 seconds
  }
  
  /**
   * Gets user-friendly error message for common scenarios
   */
  static getQuickMessage(error: ExpenseError): string {
    const quickMessages: Partial<Record<ExpenseErrorType, string>> = {
      [ExpenseErrorType.REQUIRED_FIELD_MISSING]: 'Please fill in all required fields',
      [ExpenseErrorType.INVALID_FORMAT]: 'Please check the format of your input',
      [ExpenseErrorType.VALUE_OUT_OF_RANGE]: 'Please enter a valid value within the allowed range',
      [ExpenseErrorType.CATEGORY_NOT_FOUND]: 'The selected category is not available',
      [ExpenseErrorType.DUPLICATE_CATEGORY]: 'A category with this name already exists',
      [ExpenseErrorType.EXPENSE_NOT_FOUND]: 'The expense could not be found',
      [ExpenseErrorType.RATE_LIMIT_EXCEEDED]: 'Too many requests - please wait before trying again',
      [ExpenseErrorType.NETWORK_ERROR]: 'Connection problem - please check your internet',
      [ExpenseErrorType.AUTHENTICATION_REQUIRED]: 'Please log in to continue',
      [ExpenseErrorType.INSUFFICIENT_PERMISSIONS]: 'You don\'t have permission for this action',
      [ExpenseErrorType.INTERNAL_ERROR]: 'Server error - please try again later',
      [ExpenseErrorType.UNKNOWN_ERROR]: 'Something went wrong - please try again'
    };
    
    return quickMessages[error.type] || error.message;
  }
  
  /**
   * Gets suggested solutions for common error types
   */
  static getSuggestedSolutions(error: ExpenseError): string[] {
    const solutions: Partial<Record<ExpenseErrorType, string[]>> = {
      [ExpenseErrorType.REQUIRED_FIELD_MISSING]: [
        'Check that all required fields are filled in',
        'Look for fields marked with an asterisk (*)',
        'Make sure you\'ve selected a category and entered an amount'
      ],
      [ExpenseErrorType.INVALID_FORMAT]: [
        'Check the format of dates (use YYYY-MM-DD)',
        'Ensure amounts are positive numbers',
        'Verify that text fields don\'t exceed character limits'
      ],
      [ExpenseErrorType.VALUE_OUT_OF_RANGE]: [
        'Check that amounts are positive and reasonable',
        'Ensure dates are not too far in the future',
        'Verify that text doesn\'t exceed maximum length'
      ],
      [ExpenseErrorType.CATEGORY_NOT_FOUND]: [
        'Try refreshing the page to reload categories',
        'Select a different category',
        'Create a new custom category if needed'
      ],
      [ExpenseErrorType.DUPLICATE_CATEGORY]: [
        'Choose a different name for your custom category',
        'Check if a similar category already exists',
        'Use the existing category instead of creating a new one'
      ],
      [ExpenseErrorType.NETWORK_ERROR]: [
        'Check your internet connection',
        'Try refreshing the page',
        'Wait a moment and try again'
      ],
      [ExpenseErrorType.RATE_LIMIT_EXCEEDED]: [
        'Wait a few minutes before trying again',
        'Avoid making too many requests quickly',
        'Try again during off-peak hours'
      ],
      [ExpenseErrorType.AUTHENTICATION_REQUIRED]: [
        'Log out and log back in',
        'Check that your session hasn\'t expired',
        'Clear your browser cache and cookies'
      ]
    };

    return solutions[error.type] || [
      'Try the operation again',
      'Refresh the page',
      'Contact support if the problem persists'
    ];
  }
  
  /**
   * Logs error for debugging purposes
   */
  static logError(error: ExpenseError, context?: Record<string, any>): void {
    const logData = {
      type: error.type,
      field: error.field,
      message: error.message,
      statusCode: error.statusCode,
      retryable: error.retryable,
      context: { ...error.context, ...context }
    };
    
    if (error.statusCode && error.statusCode >= 500) {
      console.error('Expense error (server):', logData);
    } else if (error.statusCode && error.statusCode >= 400) {
      console.warn('Expense error (client):', logData);
    } else {
      console.info('Expense info:', logData);
    }
  }
}

/**
 * Client-side validation utilities
 */
export class ExpenseClientValidator {
  
  /**
   * Validates expense form data on the client side
   */
  static validateExpenseForm(formData: any): ValidationResult {
    const errors: ExpenseError[] = [];
    const warnings: ExpenseError[] = [];
    
    // Required field validations
    if (!formData.categoryId) {
      errors.push({
        type: ExpenseErrorType.REQUIRED_FIELD_MISSING,
        field: 'categoryId',
        message: 'Category is required',
        suggestedAction: 'Please select an expense category'
      });
    }
    
    if (!formData.amount || formData.amount <= 0) {
      errors.push({
        type: ExpenseErrorType.VALUE_OUT_OF_RANGE,
        field: 'amount',
        message: 'Amount must be greater than 0',
        suggestedAction: 'Please enter a positive amount'
      });
    } else if (formData.amount > 1000000) {
      errors.push({
        type: ExpenseErrorType.VALUE_OUT_OF_RANGE,
        field: 'amount',
        message: 'Amount cannot exceed $1,000,000',
        suggestedAction: 'Please enter a reasonable amount'
      });
    }
    
    if (!formData.currency) {
      errors.push({
        type: ExpenseErrorType.REQUIRED_FIELD_MISSING,
        field: 'currency',
        message: 'Currency is required',
        suggestedAction: 'Please select a currency'
      });
    }
    
    if (!formData.date) {
      errors.push({
        type: ExpenseErrorType.REQUIRED_FIELD_MISSING,
        field: 'date',
        message: 'Date is required',
        suggestedAction: 'Please select a date for the expense'
      });
    } else {
      const date = new Date(formData.date);
      if (isNaN(date.getTime())) {
        errors.push({
          type: ExpenseErrorType.INVALID_FORMAT,
          field: 'date',
          message: 'Invalid date format',
          suggestedAction: 'Please use a valid date format'
        });
      } else {
        const now = new Date();
        const maxFutureDate = new Date();
        maxFutureDate.setFullYear(now.getFullYear() + 1);
        
        if (date > maxFutureDate) {
          errors.push({
            type: ExpenseErrorType.INVALID_DATE_RANGE,
            field: 'date',
            message: 'Date cannot be more than 1 year in the future',
            suggestedAction: 'Please select a date within the next year'
          });
        }
      }
    }
    
    // Description length validation
    if (formData.description && formData.description.length > 500) {
      errors.push({
        type: ExpenseErrorType.VALUE_OUT_OF_RANGE,
        field: 'description',
        message: 'Description cannot exceed 500 characters',
        suggestedAction: 'Please shorten the description'
      });
    }
    
    // Recurrence validation
    if (formData.recurrenceType !== 'one_time' && !formData.recurrenceConfig) {
      errors.push({
        type: ExpenseErrorType.REQUIRED_FIELD_MISSING,
        field: 'recurrenceConfig',
        message: 'Recurrence configuration is required for recurring expenses',
        suggestedAction: 'Please configure the recurrence settings'
      });
    }
    
    if (formData.recurrenceConfig) {
      const config = formData.recurrenceConfig;
      
      if (!config.frequency) {
        errors.push({
          type: ExpenseErrorType.REQUIRED_FIELD_MISSING,
          field: 'recurrenceConfig.frequency',
          message: 'Recurrence frequency is required',
          suggestedAction: 'Please select how often this expense recurs'
        });
      }
      
      if (!config.startDate) {
        errors.push({
          type: ExpenseErrorType.REQUIRED_FIELD_MISSING,
          field: 'recurrenceConfig.startDate',
          message: 'Start date is required for recurring expenses',
          suggestedAction: 'Please select when the recurrence should start'
        });
      }
      
      if (formData.recurrenceType === 'fixed_recurring' && (!config.amount || config.amount <= 0)) {
        errors.push({
          type: ExpenseErrorType.REQUIRED_FIELD_MISSING,
          field: 'recurrenceConfig.amount',
          message: 'Amount is required for fixed recurring expenses',
          suggestedAction: 'Please specify the recurring amount'
        });
      }
      
      if (formData.recurrenceType === 'variable_recurring' && !config.estimatedAmountRange) {
        warnings.push({
          type: ExpenseErrorType.VALIDATION_ERROR,
          field: 'recurrenceConfig.estimatedAmountRange',
          message: 'Estimated amount range is recommended for variable recurring expenses',
          suggestedAction: 'Consider adding an estimated range for better tracking'
        });
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }
  
  /**
   * Validates category form data
   */
  static validateCategoryForm(formData: any): ValidationResult {
    const errors: ExpenseError[] = [];
    
    if (!formData.name || !formData.name.trim()) {
      errors.push({
        type: ExpenseErrorType.REQUIRED_FIELD_MISSING,
        field: 'name',
        message: 'Category name is required',
        suggestedAction: 'Please enter a name for the category'
      });
    } else if (formData.name.length > 50) {
      errors.push({
        type: ExpenseErrorType.VALUE_OUT_OF_RANGE,
        field: 'name',
        message: 'Category name cannot exceed 50 characters',
        suggestedAction: 'Please use a shorter name'
      });
    }
    
    if (formData.description && formData.description.length > 200) {
      errors.push({
        type: ExpenseErrorType.VALUE_OUT_OF_RANGE,
        field: 'description',
        message: 'Description cannot exceed 200 characters',
        suggestedAction: 'Please shorten the description'
      });
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

/**
 * Hook for handling expense errors with automatic retry logic
 */
export function useExpenseErrorHandler() {
  const handleError = (error: any, context?: Record<string, any>) => {
    const expenseError = ExpenseErrorHandler.parseApiError(error);
    ExpenseErrorHandler.logError(expenseError, context);
    return expenseError;
  };
  
  const formatError = (error: ExpenseError) => {
    return ExpenseErrorHandler.formatForDisplay(error);
  };
  
  const shouldRetry = (error: ExpenseError, attemptCount: number = 0) => {
    return ExpenseErrorHandler.shouldAutoRetry(error, attemptCount);
  };
  
  const getRetryDelay = (error: ExpenseError, attemptCount: number = 0) => {
    return ExpenseErrorHandler.getRetryDelay(error, attemptCount);
  };
  
  const validateForm = (formData: any) => {
    return ExpenseClientValidator.validateExpenseForm(formData);
  };
  
  const validateCategory = (categoryData: any) => {
    return ExpenseClientValidator.validateCategoryForm(categoryData);
  };
  
  return {
    handleError,
    formatError,
    shouldRetry,
    getRetryDelay,
    validateForm,
    validateCategory
  };
}